/**
 * SpeciesReach (26 Oct 2026, v2) — "N+ app users are interested in your species".
 * variant 'box'  : advertiser dashboard (overview)
 * variant 'line' : push notification form (who will receive it)
 * Counts are rounded DOWN to 5 ("10+") and never shown below 5. Data: callable getMySpeciesStats (the caller's own species only).
 * v2: the box always shows something — a hint when no species are chosen yet, a short note if the numbers can't be
 *     loaded (with the error code, so it can be diagnosed). The dashboard and the form are never blocked.
 */
import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase-config';
import { getSpeciesName } from '../../shared/speciesUtils';

const spName = (id) => { const n = getSpeciesName(id); return n && n !== 'Unknown' ? n : `Species ${id}`; };

let cached = null;
function load() {
  if (!cached) {
    cached = httpsCallable(functions, 'getMySpeciesStats')({})
      .then((r) => ({ ok: true, data: r.data || null }))
      .catch((e) => {
        console.warn('getMySpeciesStats failed:', e && (e.code || e.message));
        cached = null;
        return { ok: false, code: (e && (e.code || e.message)) || 'unknown' };
      });
  }
  return cached;
}

const boxStyle = { display: 'flex', gap: 12, alignItems: 'flex-start', background: 'rgba(79,195,247,0.08)',
  border: '1px solid rgba(79,195,247,0.35)', borderRadius: 12, padding: '14px 16px', margin: '16px 0', color: 'inherit' };

export default function SpeciesReach({ variant = 'box' }) {
  const [res, setRes] = useState(null);
  useEffect(() => {
    let alive = true;
    load().then((d) => { if (alive) setRes(d); });
    return () => { alive = false; };
  }, []);
  if (!res) return null; // still loading
  const s = res.ok ? res.data : null;

  if (variant === 'line') {
    if (!s || !s.hasSpecies) return null;
    return (
      <p style={{ marginTop: 6 }}>
        {s.reachable
          ? <><b>{s.reachable}+</b> app users interested in your species currently have notifications on.</>
          : <>Fewer than 5 app users interested in your species have notifications on so far — the audience is growing.</>}
      </p>
    );
  }

  let main;
  let detail = null;
  if (!res.ok) {
    main = <>Audience numbers are not available right now.</>;
    detail = <span style={{ opacity: 0.6 }}>(code: {String(res.code)})</span>;
  } else if (!s || !s.hasSpecies) {
    main = <>Choose the species you show on your tours (Company Profile) to see how many Cetalingua FM app users follow them.</>;
  } else {
    main = s.interested
      ? <><b>{s.interested}+</b> Cetalingua FM app users are interested in your species.</>
      : <>Cetalingua FM is just getting started for your species — fewer than 5 app users have chosen them so far.</>;
    const shown = (s.perSpecies || []).filter((p) => p.users);
    if (shown.length) detail = shown.map((p) => `${spName(p.id)}: ${p.users}+`).join(' · ');
  }

  return (
    <div style={boxStyle}>
      <div style={{ fontSize: 26, lineHeight: 1 }}>🐋</div>
      <div>
        <div style={{ fontSize: 16 }}>{main}</div>
        {detail && <div style={{ fontSize: 14, opacity: 0.8, marginTop: 4 }}>{detail}</div>}
        <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>
          Your content and push notifications are matched by species, wherever the users live. Numbers update automatically.
        </div>
      </div>
    </div>
  );
}
